import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';

import Layout from '../Layout/Layout';
import LazyImage from '../SharedComponents/ShowMedia/LazyImage';
import PreviewMediaDialog from '../SharedComponents/ShowMedia/PreviewMediaDialog';
import EmptyState from '../SharedComponents/Forms/EmptyState';
import QuickShowWizard from '../SharedComponents/QuickShow/QuickShowWizard';
import FloatingActionButton from '../SharedComponents/FloatingActionButton/FloatingActionButton';
import FirebaseMediaListener from '../Services/FirebaseMediaListener';

import UploadPopup from '../Media/components/UploadPopup/UploadPopup';
import UpdateMedia from '../Media/components/MediaLayout/UpdateMedia';
import DeleteMediaDialog from '../Media/components/MediaLayout/DeleteMediaDialog';
import YouTubeSearchDialog from './YouTubeSearchDialog';

import { useTitle, formatDuration } from '../../config/global';
import { useGalleryContext } from '../Contexts/GalleryContext';
import { useDeleteMedia, useGetMediaUsage } from '../../queries';
import { useFetchDataByPathMutation } from '../../queries/dataTable';
import { replaceData, appendData, setParams, setRerender, deleteItemLocally, galleryRefetch } from '../../redux/Slices/scrollSlice';
import { hasMorePages } from './linksPaging';
import { toggleDialog } from '../../redux/Slices/Global/globalSlice';
import { LINK_DEFAULT_FILTERS, LINKS_TYPE_OPTIONS, SORT_OPTIONS } from '../../config/media';

import '../Media/css/mediaGallery.css';
import './css/linksGallery.css';
import { formatDateTime } from '../../utils/datetime';

/**
 * Links Gallery — 2026 redesign.
 *
 * Shares the Redux scroll slice with Media (same backend endpoint, just
 * `section: 'LINKS'`) and the same dialog flags (uploadMedia, updateMedia,
 * deleteMediaDialog). Visual layer rebuilt to match MediaGallery.
 */
export default function LinksGallery() {
  useTitle('Links');
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { layout, setLayout, selectedItem, setSelectedItem } = useGalleryContext();
  const { isFetching, data, params, rerender } = useSelector((s) => s.scroll);
  const { openDialog } = useSelector((s) => s.global);

  const fetcher        = useFetchDataByPathMutation();
  const fetchMediaUsage = useGetMediaUsage();
  const deleteMedia    = useDeleteMedia();

  const [filters, setFilters] = useState({ ...LINK_DEFAULT_FILTERS, section: 'LINKS' });
  const [menuItem, setMenuItem]     = useState(null);
  const [quickItem, setQuickItem]   = useState(null);
  const [ytOpen, setYtOpen]         = useState(false);

  /** Open the unified TV-frame preview (the same dialog Media uses). */
  const openPreview = (item) => {
    setSelectedItem(item.id);
    if (!openDialog?.previewMedia) dispatch(toggleDialog('previewMedia'));
  };

  const rowMenuRef     = useRef(null);
  const sortMenuRef    = useRef(null);
  const filterMenuRef  = useRef(null);

  const items = Array.isArray(data) ? data : [];
  const search = filters.search || '';

  const visibleItems = useMemo(() => {
    let arr = items;
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((it) => (it.file_name || '').toLowerCase().includes(q));
    }
    if (filters.typeFilter) {
      arr = arr.filter((it) => it.type === filters.typeFilter);
    }
    return arr;
  }, [items, search, filters.typeFilter]);

  // Initial fetch + filter changes. Debounced so search keystrokes don't
  // hammer the endpoint.
  useEffect(() => {
    const tid = setTimeout(() => {
      dispatch(setParams({ ...filters, section: 'LINKS' }));
    }, 300);
    return () => clearTimeout(tid);
  }, [filters]);

  // build 407: page 1 replaces the list, the next pages are appended as the user scrolls (it used to stop at the 35 newest links)
  const [pg, setPg] = useState({ page: 1, last: 1, loading: false });
  const reqRef = useRef(0);
  const sentinelRef = useRef(null);

  const refetch = () => {
    const my = ++reqRef.current;
    return fetcher.mutateAsync(
      { path: '/folder/media', pageNum: 1, params: { ...params, section: 'LINKS' } },
      {
        onSuccess: (res) => {
          if (my !== reqRef.current) return;
          dispatch(replaceData(res?.data ?? []));
          setPg({ page: 1, last: res?.pagination?.total_pages ?? 1, loading: false });
        },
      },
    );
  };

  const loadMore = async () => {
    if (pg.loading || !hasMorePages(pg.page, pg.last)) return;
    const my = reqRef.current;
    const next = pg.page + 1;
    setPg((p) => ({ ...p, loading: true }));
    try {
      const res = await fetcher.mutateAsync({ path: '/folder/media', pageNum: next, params: { ...params, section: 'LINKS' } });
      if (my !== reqRef.current) return;
      dispatch(appendData(res?.data ?? []));
      setPg({ page: next, last: res?.pagination?.total_pages ?? pg.last, loading: false });
    } catch {
      setPg((p) => ({ ...p, loading: false }));
    }
  };

  // the sentinel under the list: when it comes into view (or nearly), load the next page
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => { if (entries[0]?.isIntersecting) loadMore(); }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pg.page, pg.last, pg.loading, visibleItems.length]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  /* `galleryRefetch()` (dispatched after a successful upload / link
   * creation) flips this flag; without re-running the fetch the new
   * item only appeared on manual reload. */
  useEffect(() => {
    if (!rerender) return;
    refetch();
    dispatch(setRerender(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerender]);

  // ── Per-item ⋮ menu ─────────────────────────────────────────────────────
  const openRowMenu = (e, item) => {
    setMenuItem(item);
    setSelectedItem(item.id);
    rowMenuRef.current?.toggle(e);
  };
  const rowMenuModel = useMemo(() => {
    if (!menuItem) return [];
    return [
      { label: t('quick_show.title'), icon: 'pi pi-desktop', command: () => setQuickItem(menuItem) },
      { label: t('buttons.edit'),    icon: 'pi pi-pencil',  command: () => dispatch(toggleDialog('updateMedia')) },
      { separator: true },
      { label: t('buttons.delete'), icon: 'pi pi-trash',
        command: () => fetchMediaUsage.mutate({ id: menuItem.id }, {
          onSuccess: () => dispatch(toggleDialog('deleteMediaDialog')),
        }),
      },
    ];
  }, [menuItem, t]);

  // ── Sort / Filter popup models ─────────────────────────────────────────
  const sortMenuItems = (SORT_OPTIONS || []).map((opt) => ({
    label: opt.label,
    icon: filters.sortOrder === opt.value ? 'pi pi-check' : 'pi pi-circle',
    command: () => setFilters((f) => ({ ...f, sortOrder: opt.value })),
  }));
  const filterMenuItems = [
    {
      label: t('links_.all_types'),
      icon: !filters.typeFilter ? 'pi pi-check' : 'pi pi-circle',
      command: () => setFilters((f) => ({ ...f, typeFilter: '' })),
    },
    ...((LINKS_TYPE_OPTIONS || []).map((opt) => ({
      label: opt.label,
      icon: filters.typeFilter === opt.value ? 'pi pi-check' : 'pi pi-circle',
      command: () => setFilters((f) => ({ ...f, typeFilter: opt.value })),
    }))),
  ];

  const openCreate = () => dispatch(toggleDialog('uploadMedia'));

  // ── MobileBar ───────────────────────────────────────────────────────────
  const MobileBar = (
    <div className="pp-mg-mobile-bar d-md-none">
      <div className="pp-mg-mb-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('links_.search_placeholder')}
          value={search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
      </div>
      <div className="pp-mg-mb-actions">
        <button className="pp-mg-mb-upload" onClick={openCreate}>
          <i className="pi pi-plus" />
          <span className="pp-mg-mb-upload-label">{t('buttons.add')}</span>
        </button>
        <button className="pp-mg-mb-icon" onClick={(e) => sortMenuRef.current?.toggle(e)} aria-haspopup aria-label={t('common.sort_by')} title={t('common.sort_by')}>
          <i className="pi pi-sort-alt" />
        </button>
        <button className={`pp-mg-mb-icon ${filters.typeFilter ? 'is-active' : ''}`} onClick={(e) => filterMenuRef.current?.toggle(e)} aria-haspopup aria-label={t('common.filter')} title={t('common.filter')}>
          <i className="pi pi-filter" />
        </button>
        <div className="pp-view-toggle pp-mg-mb-view" role="group" aria-label={t('links_.view_mode')}>
          <button type="button" className={layout === 'grid' ? 'is-active' : ''} onClick={() => setLayout('grid')} title={t('links_.grid_view')}><i className="pi pi-th-large" /></button>
          <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} title={t('links_.list_view')}><i className="pi pi-list" /></button>
        </div>
      </div>
      <Menu model={sortMenuItems}   popup ref={sortMenuRef}   appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
      <Menu model={filterMenuItems} popup ref={filterMenuRef} appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
    </div>
  );

  // ── Desktop bars ───────────────────────────────────────────────────────
  const TopBar = (
    <div className="pp-media-topbar d-none d-md-flex">
      <div className="pp-media-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('links_.search_placeholder')}
          value={search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
      </div>
      <div className="pp-media-topbar-actions">
        <button
          className="pp-media-tool-btn"
          onClick={() => setYtOpen(true)}
          title={t('links_.search_youtube')}
        >
          <i className="fab fa-youtube" style={{ color: '#FF0000' }} /> {t('links_.search_youtube')}
        </button>
        <button className="pp-media-upload-btn" onClick={openCreate}>
          <i className="pi pi-plus" /> {t('links_.add_link')}
        </button>
      </div>
    </div>
  );
  const Toolbar = (
    <div className="pp-media-toolbar d-none d-md-flex">
      <button className="pp-media-tool-btn" onClick={(e) => sortMenuRef.current?.toggle(e)}>
        <i className="pi pi-sort-alt" /> {t('common.sort_by')}
      </button>
      <button className={`pp-media-tool-btn ${filters.typeFilter ? 'is-active' : ''}`} onClick={(e) => filterMenuRef.current?.toggle(e)}>
        <i className="pi pi-filter" /> {filters.typeFilter || t('common.filter')}
      </button>
      <div className="pp-media-toolbar-spacer" />
      <div className="pp-view-toggle" role="group" aria-label={t('links_.view_mode')}>
        <button type="button" className={layout === 'grid' ? 'is-active' : ''} onClick={() => setLayout('grid')} title={t('links_.grid_view')}><i className="pi pi-th-large" /></button>
        <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} title={t('links_.list_view')}><i className="pi pi-list" /></button>
      </div>
    </div>
  );

  return (
    <Layout padding={false}>
      <FirebaseMediaListener path={"/Companies/" + localStorage.getItem("company_id") + "/actions"} getData={() => dispatch(galleryRefetch())} />
      <div className="pp-media-page">
        {MobileBar}
        {TopBar}
        {Toolbar}

        <div className="pp-media-body pp-fab-safe-bottom">
          {isFetching && visibleItems.length === 0 ? (
            <div className="pp-media-loading">
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" />
            </div>
          ) : visibleItems.length === 0 ? (
            <EmptyState
              icon="pi-link"
              title={t('links_.empty_title')}
              description={t('links_.empty_desc')}
              cta={{ icon: 'pi pi-plus', label: t('links_.add_link'), onClick: openCreate }}
            />
          ) : layout === 'grid' ? (
            <LinksGrid items={visibleItems} onActivate={openPreview} onMenu={openRowMenu} onQuickShow={setQuickItem} t={t} />
          ) : (
            <LinksList items={visibleItems} onActivate={openPreview} onMenu={openRowMenu} onQuickShow={setQuickItem} t={t} />
          )}
          {hasMorePages(pg.page, pg.last) && (
            <div ref={sentinelRef} className="pp-links-more" style={{ textAlign: 'center', padding: '16px 0' }}>
              {pg.loading
                ? <ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" />
                : <button type="button" className="pp-mg-mb-icon" onClick={loadMore}>{t('links_.load_more')}</button>}
            </div>
          )}
        </div>
      </div>

      <Menu model={rowMenuModel} popup ref={rowMenuRef} className="pp-media-row-menu" appendTo={typeof document !== 'undefined' ? document.body : null} />

      {/* Existing dialogs */}
      <div className="pp-media-dialog-host">
        <UploadPopup content="links" />
      </div>
      {openDialog?.updateMedia && selectedItem && (
        <UpdateMedia item={items.find((i) => i.id === selectedItem)} />
      )}
      {openDialog?.deleteMediaDialog && selectedItem && fetchMediaUsage?.data && (
        <DeleteMediaDialog
          usage={fetchMediaUsage.data}
          isLoading={deleteMedia.isLoading || fetchMediaUsage.isLoading}
          deleteHandler={() => deleteMedia.mutate({ id: selectedItem }, {
            onSuccess: () => {
              dispatch(deleteItemLocally(selectedItem));
              dispatch(toggleDialog('deleteMediaDialog'));
            },
          })}
          data={items.find((i) => i.id === selectedItem)}
        />
      )}

      <QuickShowWizard
        open={!!quickItem}
        onClose={() => setQuickItem(null)}
        media={quickItem ? {
          id: quickItem.id,
          title: quickItem.title || (quickItem.file_name || '').replace(/_/g, ' '),
          file_name: quickItem.file_name,
          thumb: quickItem.thumb || quickItem.thumbnail,
          type: quickItem.type,
          duration: quickItem.duration,
        } : null}
      />

      {/* Unified TV-frame preview — shared with Media so audio, image,
       * video, and YouTube/web links all open the same surface. */}
      {openDialog?.previewMedia && selectedItem && (() => {
        const item = items.find((i) => i.id === selectedItem);
        return item ? <PreviewMediaDialog data={item} /> : null;
      })()}

      <FloatingActionButton
        icon="pi pi-plus"
        label={t('links_.add_link')}
        onClick={openCreate}
      />

      <YouTubeSearchDialog visible={ytOpen} onHide={() => setYtOpen(false)} />
    </Layout>
  );
}

function getTypeMeta(type, t) {
  switch (String(type || '').toUpperCase()) {
    case 'YOUTUBE':       return { icon: 'fab fa-youtube',    label: 'YouTube',  accent: '#FF0000' };
    case 'DRIVE':         return { icon: 'fab fa-google-drive', label: 'Drive',    accent: '#4285F4' };
    case 'WEBVIEW':       return { icon: 'fas fa-globe',      label: 'Web',      accent: '#6366F1' };
    case 'EXTERNAL_LINK': return { icon: 'fas fa-link',       label: 'Link',     accent: '#94A3B8' };
    default:              return { icon: 'fas fa-link',       label: 'Link',     accent: '#94A3B8' };
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Grid view
 * ─────────────────────────────────────────────────────────────────── */
function LinksGrid({ items, onActivate, onMenu, onQuickShow, t }) {
  return (
    <div className="pp-media-grid">
      {items.map((item) => <LinkGridCard key={item.id} item={item} onActivate={onActivate} onMenu={onMenu} onQuickShow={onQuickShow} t={t} />)}
    </div>
  );
}

function LinkGridCard({ item, onActivate, onMenu, onQuickShow, t }) {
  const meta = getTypeMeta(item.type, t);
  const hasThumb = !!(item.thumb || item.thumbnail);
  return (
    <div className="pp-media-card pp-link-card" onClick={() => onActivate(item)}>
      <div className="pp-media-thumb">
        {hasThumb ? (
          <LazyImage media={item} popupType="media_preview" styleClass="pp-media-thumb-img" mountPreview={false} />
        ) : (
          <div className="pp-link-thumb-fallback" style={{ background: `linear-gradient(135deg, ${meta.accent}88, ${meta.accent})` }}>
            <i className={meta.icon} />
          </div>
        )}
        <span className="pp-link-type-badge" title={meta.label}>
          <i className={meta.icon} /> {meta.label}
        </span>
        {/* "▶" overlay for YouTube items */}
        {(item.type || '').toUpperCase() === 'YOUTUBE' && (
          <span className="pp-link-play-overlay"><i className="pi pi-play-circle" /></span>
        )}
        <button
          type="button"
          className="pp-media-card-menu"
          onClick={(e) => { e.stopPropagation(); onMenu(e, item); }}
          aria-label={t('links_.more')} title={t('links_.more')}
        ><i className="pi pi-ellipsis-v" /></button>
      </div>
      <div className="pp-media-info">
        <div className="pp-media-name" title={item.file_name}>{(item.file_name || '').replace(/_/g, ' ')}</div>
        <div className="pp-media-meta">
          <span>{meta.label}{item.duration ? ` · ${formatDuration(item.duration)}` : ''}</span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * List view
 * ─────────────────────────────────────────────────────────────────── */
function LinksList({ items, onActivate, onMenu, onQuickShow, t }) {
  return (
    <div className="pp-media-list-wrap">
      <table className="pp-media-list">
        <thead>
          <tr>
            <th className="pp-media-col-thumb"></th>
            <th className="pp-media-col-name">{t('table.name')}</th>
            <th className="pp-media-col-type d-none d-sm-table-cell">{t('table.type')}</th>
            <th className="pp-media-col-duration d-none d-md-table-cell">{t('links_.duration')}</th>
            <th className="pp-media-col-date d-none d-lg-table-cell">{t('table.date')}</th>
            <th className="pp-media-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => <LinkRow key={item.id} item={item} onActivate={onActivate} onMenu={onMenu} onQuickShow={onQuickShow} t={t} />)}
        </tbody>
      </table>
    </div>
  );
}

function LinkRow({ item, onActivate, onMenu, onQuickShow, t }) {
  const meta     = getTypeMeta(item.type, t);
  const hasThumb = !!(item.thumb || item.thumbnail);
  const name     = (item.file_name || '').replace(/_/g, ' ');

  return (
    <tr className="pp-media-row">
      <td className="pp-media-col-thumb">
        <div className="pp-media-list-thumb pp-link-list-thumb" onClick={() => onActivate(item)}>
          {hasThumb
            ? <LazyImage media={item} popupType="media_preview" styleClass="pp-media-list-thumb-img" mountPreview={false} />
            : <i className={meta.icon} style={{ color: meta.accent }} />}
          {(item.type || '').toUpperCase() === 'YOUTUBE' && (
            <span className="pp-link-row-play"><i className="pi pi-play-circle" /></span>
          )}
        </div>
      </td>
      <td className="pp-media-col-name" onClick={() => onActivate(item)}>
        <span className="pp-media-row-name" title={name}>{name}</span>
        <span className="pp-media-row-sub d-md-none">
          {meta.label}{item.duration ? ` · ${formatDuration(item.duration)}` : ''}
        </span>
      </td>
      <td className="pp-media-col-type d-none d-sm-table-cell">{meta.label}</td>
      <td className="pp-media-col-duration d-none d-md-table-cell">
        {item.duration ? formatDuration(item.duration) : <span className="text-muted">—</span>}
      </td>
      <td className="pp-media-col-date d-none d-lg-table-cell">{formatDateTime(item.created_at)}</td>
      <td className="pp-media-col-actions">
        <div className="pp-media-row-actions">
          <button className="pp-media-row-action pp-media-row-primary" onClick={(e) => { e.stopPropagation(); onQuickShow(item); }} title={t('quick_show.title')} aria-label={t('quick_show.title')}>
            <i className="pi pi-desktop" />
          </button>
          <button className="pp-media-row-action" onClick={(e) => onMenu(e, item)} title={t('links_.more')} aria-label={t('links_.more')}>
            <i className="pi pi-ellipsis-v" />
          </button>
        </div>
      </td>
    </tr>
  );
}

